import { Controller } from "@hotwired/stimulus"

// Mobile Turbo Native calendar (CONEK-4562 / CONEK-4671 P2 calendar polish).
//
// 1. Fits the calendar to the viewport (CONEK-7913). The page ancestors are all
//    `min-h-*`, so the grid container never got a bounded height: the whole
//    DOCUMENT scrolled, and opening at the current hour pushed the Day/Week/Month
//    switcher and the Prev/Today/Next bar up under the page header, out of reach.
//    Pinning the calendar below the header (fixed, down to the bottom of the
//    WebView — the bottom nav is native chrome outside it) leaves the grid
//    container as the only scroller.
// 2. Opens the day/week grid at the current hour when it shows today (centre
//    the now-line), else at 08:00 — like the desktop calendar. Scrolls the grid
//    container only, never the document. Month view has neither anchor.
//    The native shell preloads background tabs, so the calendar can connect
//    while its WebView is still 0px tall (innerHeight 0 — the grid then still
//    measures its own bottom padding, so its height is no signal). A scroll
//    then is a no-op: defer it to the first resize that gives the WebView a
//    real height, once per connect.
// 3. Re-pins when something ABOVE the calendar changes height without a window
//    resize (the shell header, tabs): a ResizeObserver on every element before
//    it, up the ancestor chain. The calendar is fixed, so its own box never
//    reports those changes. A re-pin keeps the grid's scroll position.
export default class extends Controller {
  static targets = ["scroll"]

  connect() {
    this._scrolled = false
    this._onResize = this._onResize.bind(this)
    window.addEventListener("resize", this._onResize)
    this._observeAbove()
    this._onResize()
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize)
    this._observer?.disconnect()
    this._observer = null
    // Unpin, so an element that outlives the controller is not left fixed. (A
    // Turbo cache snapshot is taken while still connected and keeps the pin;
    // that is harmless — on restore the controller reconnects and re-pins.)
    Object.assign(this.element.style, {
      position: "",
      top: "",
      left: "",
      right: "",
      bottom: "",
    })
  }

  _observeAbove() {
    if (typeof ResizeObserver === "undefined") return
    this._observer = new ResizeObserver(() => this._fit())
    for (
      let node = this.element;
      node && node !== document.body;
      node = node.parentElement
    ) {
      for (
        let sibling = node.previousElementSibling;
        sibling;
        sibling = sibling.previousElementSibling
      ) {
        this._observer.observe(sibling)
      }
    }
  }

  _onResize() {
    this._fit()
    if (this._scrolled || window.innerHeight === 0) return
    this._scrolled = true
    this._scrollToWorkingHours()
  }

  // Measures the calendar's natural top by un-pinning it for a moment. While
  // un-pinned the grid container has no bounded height, so the browser clamps
  // its scrollTop to 0 — and the ResizeObserver fires once per observed
  // element right after connect, i.e. AFTER the scroll to 08:00 / the now-line.
  // Keep the grid's scroll position across the re-pin.
  _fit() {
    const scroller = this.hasScrollTarget ? this.scrollTarget : null
    const scrollTop = scroller ? scroller.scrollTop : 0
    const style = this.element.style
    style.position = ""
    style.top = ""
    // Its top in the document, not the viewport: a scrolled page would
    // otherwise pin it that much too high, over the header.
    const top = Math.max(
      0,
      this.element.getBoundingClientRect().top + window.scrollY,
    )
    Object.assign(style, {
      position: "fixed",
      top: `${top}px`,
      left: "0",
      right: "0",
      bottom: "0",
    })
    if (scroller) scroller.scrollTop = scrollTop
  }

  _scrollToWorkingHours() {
    if (!this.hasScrollTarget) return
    const scroller = this.scrollTarget
    const nowLine = scroller.querySelector(
      "[data-now-line], [data-ui--calendar--now-line-tick-target='line']",
    )
    const anchor = nowLine || scroller.querySelector('[data-hour="8"]')
    if (!anchor) return

    // The grids' sticky block (day names + all-day row) sits at the top of the
    // scroller: keep 08:00 below it instead of under it.
    const header = scroller.querySelector(
      "[data-grid-sticky-header], [data-week-header]",
    )
    const headerHeight = header ? header.getBoundingClientRect().height : 0
    const top =
      anchor.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop
    scroller.scrollTop = nowLine
      ? Math.max(0, top - scroller.clientHeight / 2)
      : Math.max(0, top - headerHeight)
  }
}
